import { defaultWhenEmpty, ifElse, isNumberHaveValue } from 'aa_common/front-end/helpers'
import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { FORM_MODES } from 'common/models'
import { useFormikContext } from 'formik'
import get from 'lodash/get'
import { MutableRefObject, useEffect, useRef } from 'react'

import { getFormMode } from '../helper'
import { getDefaultServiceLife } from '../helpers/service-life'

const DEFAULT_SERVICE_LIFE_CODE = 3

export const useSelectedAssetCategoryChange = (
  formMode: FORM_MODES,
  selectedAssetCategory: any,
  reflectedAssetCategoryIdRef?: MutableRefObject<number | null>,
  isDraftFA?: boolean
) => {
  const { values, setFieldValue } = useFormikContext()
  const valuesRef = useRef(values)
  valuesRef.current = values

  const { isNewMode } = getFormMode(formMode)
  const firstTime = useRef(true)

  useEffect(() => {
    if (selectedAssetCategory || isNewMode) {
      if (firstTime.current) {
        firstTime.current = false
        return
      }

      if (reflectedAssetCategoryIdRef?.current !== null && reflectedAssetCategoryIdRef?.current !== undefined) {
        // A reflect is in progress. The category fetch first goes through a loading state where the
        // data is undefined (which also triggers this effect in new mode), then the reflected
        // category arrives. While the loaded category is still the reflected one, skip applying the
        // category defaults so the AI-reflected values are preserved. The guard stays set (matched
        // by id) so repeated effect fires for the same category are all skipped; it is cleared only
        // once a different category is selected (e.g. manually), which must apply its defaults.
        if (!selectedAssetCategory) return
        if (get(selectedAssetCategory, 'id') === reflectedAssetCategoryIdRef.current) return
        reflectedAssetCategoryIdRef.current = null
      }

      const assetTypeForCorporateTaxCode = defaultWhenEmpty(
        get(valuesRef.current, ['asset_type_for_corporate_tax', 'code']),
        get(selectedAssetCategory, ['asset_type_for_corporate_tax', 'code'])
      )
      const depreciablePropertyTaxValue = defaultWhenEmpty(
        get(valuesRef.current, ['asset_type_for_depreciable_property_tax', 'code']),
        get(selectedAssetCategory, ['asset_type_for_depreciable_property_tax', 'code'])
      )
      const assetStructureForCorporateTax = defaultWhenEmpty(
        get(valuesRef.current, 'asset_structure_for_corporate_tax'),
        get(selectedAssetCategory, 'asset_structure_for_corporate_tax')
      )
      const assetDetailForCorporateTaxValue = defaultWhenEmpty(
        get(valuesRef.current, 'asset_detail_for_corporate_tax'),
        get(selectedAssetCategory, 'asset_detail_for_corporate_tax')
      )
      const depreciationMethodCode = defaultWhenEmpty(
        get(selectedAssetCategory, 'depreciation_method.code'),
        get(valuesRef.current, 'fixed_asset_ledgers[0].depreciation_method_code')
      )
      const currentDepreciationMethodCode = get(valuesRef.current, 'fixed_asset_ledgers[0].depreciation_method_code')
      const serviceLifeValue = get(selectedAssetCategory, 'service_life')

      // Keep using excise from journals so that do not get it from the selected asset category
      if (!isDraftFA) {
        const exciseId = defaultWhenEmpty(
          get(selectedAssetCategory, ['excise_id']),
          get(valuesRef.current, 'excise_id')
        )
        setFieldValue('excise_id', exciseId)
        !exciseId && setFieldValue('calculated_excise_value', undefined)
      }
      setFieldValue('asset_type_for_corporate_tax_code', assetTypeForCorporateTaxCode)
      setFieldValue('asset_type_for_depreciable_property_tax_code', depreciablePropertyTaxValue)
      setFieldValue('asset_structure_for_corporate_tax', assetStructureForCorporateTax)
      setFieldValue('asset_detail_for_corporate_tax', assetDetailForCorporateTaxValue)

      const isBulkEqualInstallment = depreciationMethodCode === DEPRECIATION_METHOD_CODE.BULK_EQUAL_INSTALLMENT_METHOD
      setFieldValue('fixed_asset_ledgers[0].depreciation_method_code', depreciationMethodCode)
      const currentServiceLifeValue = ifElse(isBulkEqualInstallment, DEFAULT_SERVICE_LIFE_CODE, serviceLifeValue)
      if (isNumberHaveValue(currentServiceLifeValue)) {
        setFieldValue('fixed_asset_ledgers[0].service_life', currentServiceLifeValue)
      } else if (depreciationMethodCode !== currentDepreciationMethodCode) {
        setFieldValue('fixed_asset_ledgers[0].service_life', getDefaultServiceLife(depreciationMethodCode))
      }
    }
  }, [selectedAssetCategory, isNewMode, isDraftFA])
}
